import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Stack, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useCallback} from 'react';

import navigateToURL from 'lib/navigateToURL';
import AgentFactoriesList from 'llm/chat-library/factories/AgentFactoriesList';
import CreateAgentFactoryModal from 'llm/chat-library/factories/CreateAgentFactoryModal';
import useAgentFactories from 'llm/chat-library/factories/useAgentFactories';
import {agentFactoryPageUrl} from 'llm/routes/UrlUtils';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    padding: `${theme.spacing.lg} ${theme.spacing.xxl}`,
    gap: theme.spacing.md,
  },
}));

/**
 * The Agent Factories list, rendered as a top-level page in the sidepanel's main content slot (the
 * same slot the chat occupies): a title, a simple "Add" button, and a listing. Selecting a row opens
 * the factory as its own workspace tab (see EditorRoutes.agentFactoryEditor), collapsing this list
 * back to the chat's normal semi-expanded view to make room for it.
 */
export default function AgentFactoriesListPage(): JSX.Element {
  const {agentFactories, isLoading, isError, refetch, createAgentFactory} = useAgentFactories();
  const {classes} = useStyles();
  const [isCreateModalOpen, {open: openCreateModal, close: closeCreateModal}] = useDisclosure(false);

  const handleSelect = useCallback((agentFactoryId: string) => {
    navigateToURL(agentFactoryPageUrl(agentFactoryId));
  }, []);

  return (
    <Stack spacing="md" className={classes.container}>
      <Group noWrap={true} position="apart" align="center">
        <Title order={3}>Factories</Title>
        <Button
          variant="secondary"
          size="xs"
          leftIcon={<BenchlingIcon icon="plus" />}
          onClick={openCreateModal}
          {...getTestProps({component: 'AgentFactoriesListPage', element: 'addButton'})}
        >
          Add
        </Button>
      </Group>
      {isError ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Failed to load factories."
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Retry',
            actionOnClick: refetch,
          }}
        />
      ) : isLoading ? (
        <CenteredSpinner />
      ) : (
        <AgentFactoriesList agentFactories={agentFactories} onSelect={handleSelect} />
      )}
      <CreateAgentFactoryModal
        opened={isCreateModalOpen}
        onRequestClose={closeCreateModal}
        onRequestSubmit={({name, description}) => createAgentFactory(name, description)}
      />
    </Stack>
  );
}
